import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { HSL } from './colors';

function scoreColor(score) {
  if (score >= 70) return HSL.success;
  if (score >= 50) return HSL.secondary;
  if (score >= 30) return HSL.warning;
  return HSL.error;
}

function DeskRow({ desk }) {
  const members = (desk.member_names || []).map((name, idx) => ({
    name,
    email: desk.member_emails?.[idx] || '',
  }));

  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} hover>
      <TableCell sx={{ width: 72, verticalAlign: 'top', color: HSL.textMuted }}>
        #{desk.number}
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ color: HSL.textStrong, fontWeight: 500 }}
          >
            {members.map((m) => m.name).join('  \u00B7  ') || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: HSL.textMuted }}>
            {members
              .map((m) => m.email)
              .filter(Boolean)
              .join('  \u00B7  ')}
          </Typography>
          {desk.why_paired && (
            <Tooltip title={desk.why_paired} arrow placement="top-start">
              <Typography
                variant="caption"
                sx={{
                  color: HSL.textMuted,
                  fontStyle: 'italic',
                  mt: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {desk.why_paired}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ width: 96, verticalAlign: 'top' }}>
        <Chip
          label={desk.match_score ?? 0}
          size="small"
          sx={{
            backgroundColor: 'transparent',
            border: `1px solid ${scoreColor(desk.match_score ?? 0)}`,
            color: scoreColor(desk.match_score ?? 0),
            fontWeight: 600,
          }}
        />
      </TableCell>
    </TableRow>
  );
}

function PeerGroupCard({ group }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '8px',
        border: `1px solid ${HSL.border}`,
        p: 2,
        mb: 2,
        backgroundColor: HSL.surface,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: HSL.textStrong }}
        >
          Peer Group #{group.number}
        </Typography>
        <Chip
          label={`${group.size} learner${group.size === 1 ? '' : 's'}`}
          size="small"
          sx={{
            backgroundColor: HSL.secondarySoft,
            color: HSL.secondary,
            fontWeight: 500,
          }}
        />
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ color: HSL.textMuted, fontWeight: 600 }}>
              Desk
            </TableCell>
            <TableCell sx={{ color: HSL.textMuted, fontWeight: 600 }}>
              Members
            </TableCell>
            <TableCell sx={{ color: HSL.textMuted, fontWeight: 600 }}>
              Score
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(group.desks || []).map((desk) => (
            <DeskRow key={`${group.number}-${desk.number}`} desk={desk} />
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function DeskiesTable({ peerGroups }) {
  if (!peerGroups || peerGroups.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: '8px',
          border: `1px dashed ${HSL.border}`,
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: HSL.textMuted }}>
          No peer groups drafted yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {peerGroups.map((group) => (
        <PeerGroupCard key={group.number} group={group} />
      ))}
    </Box>
  );
}

export default DeskiesTable;
